import React, { useEffect, useState } from "react";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Sparkles, Users, FolderKanban, FileText, LayoutDashboard } from "lucide-react";

export default function CommandPalette({ open, setOpen }) {
  const { hasModuleAccess } = useAuth();
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ leads: [], clients: [], projects: [], invoices: [] });
  const nav = useNavigate();

  const quickLinks = [
    { label: "Dashboard", path: "/", module: "Dashboard", icon: LayoutDashboard },
    { label: "Leads", path: "/leads", module: "Leads", icon: Sparkles },
    { label: "Clients", path: "/clients", module: "Clients", icon: Users },
    { label: "Projects", path: "/projects", module: "Projects", icon: FolderKanban },
    { label: "Invoices", path: "/invoices", module: "Invoices", icon: FileText },
  ].filter((item) => hasModuleAccess(item.module));

  useEffect(() => {
    if (!q.trim()) { setResults({ leads: [], clients: [], projects: [], invoices: [] }); return; }
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then((r) => setResults(r.data)).catch(() => {});
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { setOpen(false); nav(path); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput value={q} onValueChange={setQ} placeholder="Search permitted records and routes…" data-testid="command-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {quickLinks.length > 0 && (
          <CommandGroup heading="Quick navigation">
            {quickLinks.map((item) => (
              <CommandItem key={item.path} onSelect={() => go(item.path)}>
                <item.icon className="w-4 h-4 mr-2" /> {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {results.leads.length > 0 && (
          <><CommandSeparator /><CommandGroup heading="Leads">
            {results.leads.map((x) => (
              <CommandItem key={x.id} onSelect={() => go("/leads")}>
                <Sparkles className="w-4 h-4 mr-2 text-bronze" />
                <span>{x.name}</span><span className="ml-2 text-muted-foreground">· {x.company}</span>
                <span className="ml-auto text-xs text-muted-foreground">{x.stage}</span>
              </CommandItem>
            ))}
          </CommandGroup></>
        )}
        {results.clients.length > 0 && (
          <><CommandSeparator /><CommandGroup heading="Clients">
            {results.clients.map((x) => (
              <CommandItem key={x.id} onSelect={() => go("/clients")}>
                <Users className="w-4 h-4 mr-2 text-bronze" />
                <span>{x.company || x.name}</span>
              </CommandItem>
            ))}
          </CommandGroup></>
        )}
        {results.invoices.length > 0 && (
          <><CommandSeparator /><CommandGroup heading="Invoices">
            {results.invoices.map((x) => (
              <CommandItem key={x.id} onSelect={() => go(`/invoices/${x.id}`)}>
                <FileText className="w-4 h-4 mr-2 text-bronze" />
                <span className="font-mono">{x.invoice_number}</span>
                <span className="ml-auto text-xs text-muted-foreground">{x.status}</span>
              </CommandItem>
            ))}
          </CommandGroup></>
        )}
        {results.projects.length > 0 && (
          <><CommandSeparator /><CommandGroup heading="Projects">
            {results.projects.map((x) => (
              <CommandItem key={x.id} onSelect={() => go("/projects")}>
                <FolderKanban className="w-4 h-4 mr-2 text-bronze" />
                <span>{x.name}</span>
                <span className="ml-auto text-xs text-muted-foreground">{x.status}</span>
              </CommandItem>
            ))}
          </CommandGroup></>
        )}
      </CommandList>
    </CommandDialog>
  );
}
